import { useCallback, useEffect, useState, type PropsWithChildren } from 'react'
import {
  LANDING_THEME_MODE,
  LANDING_THEME_STORAGE_KEY,
  type TenantThemeMode,
} from '@propbix/constants/public'
import { ThemeContext, type ResolvedTheme } from '@/theme/theme-context'

function getSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined') {
    return 'light'
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function readStoredMode(): TenantThemeMode {
  if (typeof window === 'undefined') {
    return LANDING_THEME_MODE.SYSTEM
  }
  const stored = window.localStorage.getItem(LANDING_THEME_STORAGE_KEY)
  if (
    stored === LANDING_THEME_MODE.LIGHT ||
    stored === LANDING_THEME_MODE.DARK ||
    stored === LANDING_THEME_MODE.SYSTEM
  ) {
    return stored
  }
  return LANDING_THEME_MODE.SYSTEM
}

function resolveMode(mode: TenantThemeMode): ResolvedTheme {
  if (mode === LANDING_THEME_MODE.SYSTEM) {
    return getSystemTheme()
  }
  return mode === LANDING_THEME_MODE.DARK ? 'dark' : 'light'
}

function applyResolvedTheme(resolved: ResolvedTheme) {
  const root = document.documentElement
  root.classList.toggle('dark', resolved === 'dark')
  root.style.colorScheme = resolved
}

export function ThemeProvider({ children }: PropsWithChildren) {
  const [mode, setModeState] = useState<TenantThemeMode>(() => readStoredMode())
  const [resolved, setResolved] = useState<ResolvedTheme>(() => resolveMode(readStoredMode()))

  const setMode = useCallback((next: TenantThemeMode) => {
    setModeState(next)
    window.localStorage.setItem(LANDING_THEME_STORAGE_KEY, next)
    const nextResolved = resolveMode(next)
    setResolved(nextResolved)
    applyResolvedTheme(nextResolved)
  }, [])

  const toggleLightDark = useCallback(() => {
    setMode(resolved === 'dark' ? LANDING_THEME_MODE.LIGHT : LANDING_THEME_MODE.DARK)
  }, [resolved, setMode])

  useEffect(() => {
    applyResolvedTheme(resolved)
  }, [resolved])

  useEffect(() => {
    if (mode !== LANDING_THEME_MODE.SYSTEM) {
      return
    }
    const media = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = () => {
      const next = getSystemTheme()
      setResolved(next)
      applyResolvedTheme(next)
    }
    media.addEventListener('change', onChange)
    return () => media.removeEventListener('change', onChange)
  }, [mode])

  return (
    <ThemeContext.Provider value={{ mode, resolved, setMode, toggleLightDark }}>
      {children}
    </ThemeContext.Provider>
  )
}
